import { logger } from '@/utils/logger';

interface AccessibilityOptions {
  enableFocusTrapping?: boolean;
  enableKeyboardNavigation?: boolean;
  enableScreenReaderSupport?: boolean;
  enableColorContrastChecking?: boolean;
}

interface FocusTrapOptions {
  initialFocus?: HTMLElement | string;
  onEscape?: () => void;
  restoreFocus?: boolean;
}

class AccessibilityEnhancer {
  private activeTraps: Map<string, FocusTrap> = new Map();
  private keyboardListeners: Map<string, (e: KeyboardEvent) => void> = new Map();
  private contrastCache: Map<string, boolean> = new Map();

  // Focus Management
  createFocusTrap(
    container: HTMLElement | string,
    options: FocusTrapOptions = {}
  ): FocusTrap {
    const element = typeof container === 'string' 
      ? document.querySelector(container) as HTMLElement
      : container;
      
    if (!element) {
      throw new Error('Focus trap container not found');
    }

    const trap = new FocusTrap(element, options);
    const trapId = this.generateTrapId();
    this.activeTraps.set(trapId, trap);
    
    return trap;
  }

  removeFocusTrap(trap: FocusTrap | string) {
    if (typeof trap === 'string') {
      const foundTrap = this.activeTraps.get(trap);
      if (foundTrap) {
        foundTrap.destroy();
        this.activeTraps.delete(trap);
      }
    } else {
      // Find and remove by instance
      for (const [id, activeTrap] of this.activeTraps.entries()) {
        if (activeTrap === trap) {
          activeTrap.destroy();
          this.activeTraps.delete(id);
          break;
        }
      }
    }
  }

  // Keyboard Navigation
  enableKeyboardNavigation(
    container: HTMLElement | string,
    options: {
      arrowKeys?: boolean;
      tabKey?: boolean;
      escapeKey?: boolean;
      customKeys?: Record<string, () => void>;
    } = {}
  ): string {
    const element = typeof container === 'string' 
      ? document.querySelector(container) as HTMLElement
      : container;
      
    if (!element) {
      throw new Error('Navigation container not found');
    }

    const listenerId = this.generateListenerId();
    
    const keyHandler = (e: KeyboardEvent) => {
      const { arrowKeys = true, tabKey = true, escapeKey = true, customKeys = {} } = options;
      
      // Handle custom keys first
      if (customKeys[e.key]) {
        e.preventDefault();
        customKeys[e.key]();
        return;
      }
      
      // Handle escape key
      if (escapeKey && e.key === 'Escape') {
        const activeElement = document.activeElement as HTMLElement;
        if (element.contains(activeElement)) {
          activeElement.blur();
        }
        return;
      }
      
      // Handle arrow keys for navigation
      if (arrowKeys && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        this.navigateWithArrows(element, e.key);
        return;
      }
      
      // Handle tab key enhancements
      if (tabKey && e.key === 'Tab') {
        this.enhanceTabNavigation(element, e);
      }
    };
    
    element.addEventListener('keydown', keyHandler);
    this.keyboardListeners.set(listenerId, keyHandler);
    
    return listenerId;
  }

  removeKeyboardNavigation(listenerId: string) {
    const handler = this.keyboardListeners.get(listenerId);
    if (handler) {
      document.removeEventListener('keydown', handler);
      this.keyboardListeners.delete(listenerId);
    }
  }

  // Screen Reader Support
  announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite') {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.style.cssText = `
      position: absolute;
      left: -10000px;
      width: 1px;
      height: 1px;
      overflow: hidden;
    `;
    
    document.body.appendChild(announcer);
    announcer.textContent = message;
    
    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  }

  enhanceElementForScreenReader(
    element: HTMLElement,
    options: {
      label?: string;
      description?: string;
      role?: string;
      expanded?: boolean;
      selected?: boolean;
    }
  ) {
    const { label, description, role, expanded, selected } = options;
    
    if (label) {
      element.setAttribute('aria-label', label);
    }
    
    if (description) {
      const descId = `desc-${this.generateId()}`;
      const descElement = document.createElement('span');
      descElement.id = descId;
      descElement.textContent = description;
      descElement.style.cssText = `
        position: absolute;
        left: -10000px;
        width: 1px;
        height: 1px;
        overflow: hidden;
      `;
      
      document.body.appendChild(descElement);
      element.setAttribute('aria-describedby', descId);
    }
    
    if (role) {
      element.setAttribute('role', role);
    }
    
    if (expanded !== undefined) {
      element.setAttribute('aria-expanded', expanded.toString());
    }
    
    if (selected !== undefined) {
      element.setAttribute('aria-selected', selected.toString());
    }
  }

  // Color Contrast Checking
  checkColorContrast(
    foreground: string,
    background: string,
    level: 'AA' | 'AAA' = 'AA'
  ): boolean {
    const cacheKey = `${foreground}-${background}-${level}`;
    
    if (this.contrastCache.has(cacheKey)) {
      return this.contrastCache.get(cacheKey)!;
    }
    
    const ratio = this.calculateContrastRatio(foreground, background);
    const threshold = level === 'AAA' ? 7 : 4.5;
    const passes = ratio >= threshold;
    
    this.contrastCache.set(cacheKey, passes);
    
    if (!passes) {
      console.warn('Color contrast issue', {
        foreground,
        background,
        ratio,
        level,
        threshold
      });
    }
    
    return passes;
  }

  // Utility Methods
  private navigateWithArrows(container: HTMLElement, direction: string) {
    const focusableElements = this.getFocusableElements(container);
    const currentIndex = focusableElements.findIndex(el => el === document.activeElement);
    
    let nextIndex = currentIndex;
    
    switch (direction) {
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
        break;
    }
    
    if (focusableElements[nextIndex]) {
      focusableElements[nextIndex].focus();
    }
  }

  private enhanceTabNavigation(container: HTMLElement, e: KeyboardEvent) {
    const focusableElements = this.getFocusableElements(container);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement?.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement?.focus();
    }
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');
    
    return Array.from(container.querySelectorAll(selector)) as HTMLElement[];
  }

  private calculateContrastRatio(color1: string, color2: string): number {
    const rgb1 = this.hexToRgb(color1);
    const rgb2 = this.hexToRgb(color2);
    
    if (!rgb1 || !rgb2) return 1;
    
    const l1 = this.getLuminance(rgb1);
    const l2 = this.getLuminance(rgb2);
    
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    
    return (lighter + 0.05) / (darker + 0.05);
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  private getLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  private generateTrapId(): string {
    return `trap-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateListenerId(): string {
    return `listener-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateId(): string {
    return `id-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  cleanup() {
    this.activeTraps.forEach(trap => trap.destroy());
    this.activeTraps.clear();
    
    this.keyboardListeners.forEach((handler, id) => {
      this.removeKeyboardNavigation(id);
    });
    
    this.contrastCache.clear();
  }
}

class FocusTrap {
  private container: HTMLElement;
  private options: FocusTrapOptions;
  private previouslyFocused: HTMLElement | null = null;
  private isActive = false;

  constructor(container: HTMLElement, options: FocusTrapOptions = {}) {
    this.container = container;
    this.options = options;
    this.activate();
  }

  activate() {
    if (this.isActive) return;
    
    this.previouslyFocused = document.activeElement as HTMLElement;
    this.isActive = true;
    
    // Set initial focus
    if (this.options.initialFocus) {
      const target = typeof this.options.initialFocus === 'string'
        ? this.container.querySelector(this.options.initialFocus) as HTMLElement
        : this.options.initialFocus;
      
      target?.focus();
    } else {
      const firstFocusable = this.getFocusableElements()[0];
      firstFocusable?.focus();
    }
    
    this.container.addEventListener('keydown', this.handleKeyDown);
  }

  deactivate() {
    if (!this.isActive) return;
    
    this.isActive = false;
    this.container.removeEventListener('keydown', this.handleKeyDown);
    
    if (this.options.restoreFocus !== false && this.previouslyFocused) {
      this.previouslyFocused.focus();
    }
  }

  destroy() {
    this.deactivate();
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.options.onEscape) {
      e.preventDefault();
      this.options.onEscape();
      return;
    }
    
    if (e.key === 'Tab') {
      this.handleTabKey(e);
    }
  };

  private handleTabKey(e: KeyboardEvent) {
    const focusableElements = this.getFocusableElements();
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement?.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement?.focus();
    }
  }

  private getFocusableElements(): HTMLElement[] {
    const selector = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');
    
    return Array.from(this.container.querySelectorAll(selector)) as HTMLElement[];
  }
}

export const accessibilityEnhancer = new AccessibilityEnhancer();
export { FocusTrap };
export type { AccessibilityOptions, FocusTrapOptions };